import type { HistoryPoint, HistoryRange } from "@central-hub/contracts";

/** What a bucket says about the target; "none" means nothing was measured (a gap). */
export type PointStatus = "up" | "degraded" | "down" | "unknown" | "none";

/** The worst state seen in the bucket: one failed result is worth showing. */
export function pointStatus(
  p: Pick<HistoryPoint, "up" | "degraded" | "down" | "unknown">,
): PointStatus {
  if (p.down > 0) return "down";
  if (p.degraded > 0) return "degraded";
  if (p.up > 0) return "up";
  if (p.unknown > 0) return "unknown";
  return "none";
}

export interface Run {
  status: PointStatus;
  /** Inclusive point indexes. */
  start: number;
  end: number;
}

/** Consecutive buckets with the same status, merged: one mark per run, not per bucket. */
export function statusRuns(points: readonly HistoryPoint[]): Run[] {
  const runs: Run[] = [];
  points.forEach((p, i) => {
    const status = pointStatus(p);
    const last = runs.at(-1);
    if (last && last.status === status) last.end = i;
    else runs.push({ status, start: i, end: i });
  });
  return runs;
}

/** Smallest "nice" number (1, 2, 2.5, 5 × 10ⁿ) ≥ value; the y-axis top. */
export function niceCeil(value: number): number {
  if (!(value > 0)) return 1;
  const exponent = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * exponent >= value) return step * exponent;
  }
  return 10 * exponent;
}

/** 0 … max in four equal, round steps. */
export function yTicks(max: number): number[] {
  const top = niceCeil(max);
  return [0, 1, 2, 3, 4].map((i) => (top * i) / 4);
}

/** "0 ms", "250 ms", "1.5 s", "12 s". */
export function formatLatency(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  return `${seconds < 10 ? Number(seconds.toFixed(1)) : Math.round(seconds)} s`;
}

/** SVG path through the values, broken (new "M") wherever a value is missing. */
export function linePath(
  values: readonly (number | null)[],
  x: (i: number) => number,
  y: (v: number) => number,
): string {
  let path = "";
  let drawing = false;
  values.forEach((v, i) => {
    if (v === null) {
      drawing = false;
      return;
    }
    path += `${drawing ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    drawing = true;
  });
  return path;
}

/** Points that have no neighbour on either side: a lone value needs a dot to be visible. */
export function isolatedPoints(values: readonly (number | null)[]): number[] {
  return values.flatMap((v, i) =>
    v !== null && (values[i - 1] ?? null) === null && (values[i + 1] ?? null) === null ? [i] : [],
  );
}

const pad = (n: number) => String(n).padStart(2, "0");
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** X-axis ticks at round local times: every 3 h (24 h), every day (7 d), every 5 days (30 d). */
export function timeTicks(
  from: number,
  to: number,
  range: HistoryRange,
): { t: number; label: string }[] {
  const ticks: { t: number; label: string }[] = [];
  const start = new Date(from);
  if (range === "24h") {
    start.setMinutes(0, 0, 0);
    start.setHours(Math.ceil(start.getHours() / 3) * 3);
  } else {
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + 1);
  }
  const stepDays = range === "30d" ? 5 : 1;
  for (const d = new Date(start); d.getTime() < to; ) {
    if (d.getTime() >= from) {
      ticks.push({
        t: d.getTime(),
        label:
          range === "24h"
            ? `${pad(d.getHours())}:00`
            : d.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
      });
    }
    if (range === "24h") d.setHours(d.getHours() + 3);
    else d.setDate(d.getDate() + stepDays);
  }
  return ticks;
}

/** "14:05–14:10" (24 h) or "20 Oct 14:00–15:00" for one bucket, in local time. */
export function bucketLabel(t: number, bucketMs: number): string {
  const a = new Date(t);
  const b = new Date(t + bucketMs);
  const time = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (bucketMs < HOUR) return `${time(a)}–${time(b)}`;
  const day = a.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return bucketMs < DAY ? `${day} ${time(a)}–${time(b)}` : day;
}
